/**
 * HWP 5.0 record stream parsing — the part that turns a decompressed BodyText
 * section into readable paragraphs.
 *
 * An HWP body section is a flat stream of records. Each record is a 32-bit
 * header — 10 bits of tag, 10 of nesting level, 12 of size — followed by its
 * payload; a size of `0xFFF` means the real size is the next 32-bit word, so a
 * large record is not silently truncated. The only record this reader needs is
 * `PARA_TEXT` (tag `0x43`), whose payload is the paragraph's text as UTF-16LE.
 *
 * That text is not plain UTF-16: it is interrupted by control code points below
 * `0x20` that stand for inline objects, field boundaries and breaks. Some are a
 * single code unit; the "extended" ones are eight (the control, six code units
 * of information, and the control again). Reading them by their width is what
 * keeps an inline table or picture from turning six code units of binary into
 * mojibake in the middle of a sentence.
 */
export const HWPTAG_BEGIN = 0x10;
export const HWPTAG_PARA_HEADER = HWPTAG_BEGIN + 50; // 0x42
export const HWPTAG_PARA_TEXT = HWPTAG_BEGIN + 51;   // 0x43

export interface HwpRecord {
  readonly tag: number;
  readonly level: number;
  readonly start: number;
  readonly end: number;
}

/** Walk the record headers in a decompressed section, bounded by its length. */
export function* records(bytes: Uint8Array): Generator<HwpRecord> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 0;
  while (pos + 4 <= bytes.length) {
    const header = view.getUint32(pos, true);
    const tag = header & 0x3ff;
    const level = (header >> 10) & 0x3ff;
    let size = (header >> 20) & 0xfff;
    let bodyStart = pos + 4;
    if (size === 0xfff) {
      if (bodyStart + 4 > bytes.length) return;
      size = view.getUint32(bodyStart, true);
      bodyStart += 4;
    }
    const end = bodyStart + size;
    if (end > bytes.length || end < bodyStart) return; // a size that overruns (§30)
    yield { tag, level, start: bodyStart, end };
    pos = end;
  }
}

/* Control code points by how many UTF-16 code units they occupy in PARA_TEXT. */
const ONE_WIDE = new Set([0, 10, 13, 24, 25, 26, 27, 28, 29, 30, 31]);

/**
 * Decode one `PARA_TEXT` payload to a string.
 *
 * Breaks (`10`/`13`) become newlines; other single-unit controls are dropped;
 * an extended control skips its whole eight-unit run so its binary payload is
 * never mistaken for text. Everything at `0x20` or above is a real character.
 */
export function decodeParaText(bytes: Uint8Array, start: number, end: number): string {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let out = '';
  let i = start;
  while (i + 2 <= end) {
    const c = view.getUint16(i, true);
    if (c >= 0x20) { out += String.fromCharCode(c); i += 2; continue; }
    if (c === 10 || c === 13) { out += '\n'; i += 2; continue; }
    if (ONE_WIDE.has(c)) { i += 2; continue; }
    // An extended control: control + 6 info units + control = 8 code units.
    i += 16;
  }
  return out;
}

/** Every paragraph's text, in order, from a decompressed section. */
export function paragraphsOf(section: Uint8Array): string[] {
  const paragraphs: string[] = [];
  for (const record of records(section)) {
    if (record.tag === HWPTAG_PARA_TEXT) {
      paragraphs.push(decodeParaText(section, record.start, record.end));
    }
  }
  return paragraphs;
}

/* ------------------------------------------------------------------ *
 * FileHeader
 * ------------------------------------------------------------------ */

export interface HwpFileHeader {
  readonly valid: boolean;
  /** e.g. "5.0.0.0" */
  readonly version: string;
  readonly compressed: boolean;
  readonly passwordProtected: boolean;
  readonly distributed: boolean;
}

const SIGNATURE = 'HWP Document File';

/** Parse the 256-byte FileHeader stream. */
export function parseFileHeader(bytes: Uint8Array): HwpFileHeader {
  let signature = '';
  for (let i = 0; i < SIGNATURE.length && i < bytes.length; i++) signature += String.fromCharCode(bytes[i]!);
  const valid = signature === SIGNATURE;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const versionWord = bytes.length >= 36 ? view.getUint32(32, true) : 0;
  const version = `${(versionWord >> 24) & 0xff}.${(versionWord >> 16) & 0xff}.${(versionWord >> 8) & 0xff}.${versionWord & 0xff}`;
  const flags = bytes.length >= 40 ? view.getUint32(36, true) : 0;

  return {
    valid,
    version,
    compressed: (flags & 0x01) !== 0,
    passwordProtected: (flags & 0x02) !== 0,
    distributed: (flags & 0x04) !== 0,
  };
}
